/**
 * @fileoverview Organism: the Peak Maxxing plate — a massif under a low sun.
 * @module components/organisms/PeakArt
 *
 * @description
 * Hand-authored SVG rather than an asset: the drawing has to follow the
 * theme (paper and ink both flip in dark mode) and carry the same rough-edge
 * filter as its Testo counterpart, which a flat image cannot do.
 *
 * Ids are prefixed per plate: two inline SVGs on the same document share one
 * id namespace, and a duplicated `peak`/`testo` filter id would silently make
 * one of them render with the other's noise.
 */

import { MODE_TAGLINE } from "@/lib/domain"
import { AppMode } from "@/types/types"

import "@/styles/art.css"

/**
 * The Peak Maxxing illustration.
 *
 * @returns The rendered plate, labelled as an image: it is the mode's whole
 *   identity, not decoration.
 */
export function PeakArt() {
	return (
		<svg
			className="art art--peak"
			viewBox="0 0 320 220"
			role="img"
			aria-label={`Peak Maxxing — ${MODE_TAGLINE[AppMode.PEAK]}`}
		>
			<defs>
				<filter
					id="peak-rough"
					x="-12%"
					y="-12%"
					width="124%"
					height="124%"
					filterUnits="objectBoundingBox"
				>
					<feTurbulence
						type="fractalNoise"
						baseFrequency="0.045"
						numOctaves="3"
						seed="11"
						result="peak-noise"
					/>
					<feDisplacementMap
						in="SourceGraphic"
						in2="peak-noise"
						scale="3"
						xChannelSelector="R"
						yChannelSelector="G"
					/>
				</filter>

				<pattern
					id="peak-hatch"
					width="7"
					height="7"
					patternUnits="userSpaceOnUse"
					patternTransform="rotate(38)"
				>
					<line className="art__hatch" x1="0" y1="0" x2="0" y2="7" />
				</pattern>

				<pattern
					id="peak-hatch-dense"
					width="4"
					height="4"
					patternUnits="userSpaceOnUse"
					patternTransform="rotate(38)"
				>
					<line className="art__hatch" x1="0" y1="0" x2="0" y2="4" />
				</pattern>
			</defs>

			<g filter="url(#peak-rough)">
				{/* Low sun, rays drawn one by one so they stay uneven. */}
				<circle
					className="art__ink art__ink--thin"
					cx="252"
					cy="46"
					r="19"
				/>
				<g className="art__ink art__ink--hollow art__ink--thin">
					<path d="M252 14v-9M252 87v9M284 46h9M211 46h9" />
					<path d="M277 21l6-6M221 71l-6 6M277 71l6 6M221 21l-6-6" />
				</g>

				{/* Far range: thinner line, it sits further back on the plate. */}
				<path
					className="art__ink art__ink--thin"
					d="M2 170 L58 104 L96 142 L142 92 L196 148 L244 108 L318 170 Z"
				/>

				{/* Main massif. */}
				<path
					className="art__ink"
					d="M20 178 L116 44 L170 112 L208 74 L302 178 Z"
				/>

				{/* Shaded faces: the sun is on the right, so the right slopes stay
            lit and everything falling away from it gets hatched. */}
				<path
					className="art__shade"
					d="M116 44 L170 112 L170 178 L116 178 Z"
					fill="url(#peak-hatch)"
				/>
				<path
					className="art__shade"
					d="M208 74 L302 178 L232 178 Z"
					fill="url(#peak-hatch)"
				/>
				<path
					className="art__shade"
					d="M142 86 L170 112 L170 178 L152 178 Z"
					fill="url(#peak-hatch-dense)"
				/>

				{/* Snow cap: a zigzag snowline closed back onto the summit. */}
				<path
					className="art__ink art__ink--thin"
					d="M98 70 L106 79 L114 67 L123 80 L131 69 L138 77 L116 44 Z"
				/>

				{/* Ground line, and two birds for scale. */}
				<path className="art__ink art__ink--hollow" d="M0 178 H320" />
				<path
					className="art__ink art__ink--hollow art__ink--thin"
					d="M60 62 l8 -7 l8 7M96 46 l6 -5 l6 5"
				/>
			</g>
		</svg>
	)
}
